import { useEffect, useState } from "react";
import {
    listContractorNotes,
    createContractorNote,
    updateContractorNote,
    deleteContractorNote,
    listBulkUploads,
    enqueueBulkUploads,
    deleteBulkUploadJob,
    portfolioExportZipUrl,
    fetchCalibrationShareStats,
    updateNotificationPrefs,
    fetchInvestorTeamInfo,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasFeature } from "@/lib/tier";
import LockedFeatureCard from "@/components/investor/LockedFeatureCard";
import {
    Download,
    Plus,
    Trash2,
    Users,
    Star,
    FileStack,
    Award,
    Shield,
    Upload,
    Edit2,
    Check,
    X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/**
 * PlusFeaturesPanel — bundles every Plus-tier feature into one calm panel:
 *   1. Advanced portfolio export (ZIP bundle)
 *   2. Bulk upload queue (multi-PDF drag-drop + status tracker)
 *   3. Saved contractor notes (CRUD)
 *   4. Calibration share-back opt-in + "Calibrator" badge
 *   5. Team access "coming soon" preview
 *
 * Each subsection self-locks for Pro/Lite users via LockedFeatureCard.
 * Cost-Safe: no AI calls anywhere.
 */
export default function PlusFeaturesPanel() {
    const { user } = useAuth();
    const isPlus = hasFeature(user, "saved_contractor_notes");

    if (!isPlus) {
        return (
            <div className="max-w-2xl">
                <LockedFeatureCard
                    feature="saved_contractor_notes"
                    title="Plus-tier workflow"
                    description="Saved contractor notes, bulk upload queue, advanced portfolio exports, calibration share-back, and early team access. Upgrade to Investor Plus to unlock."
                />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="plus-features-panel">
            <CalibratorBadgeCard />
            <PortfolioExportCard />
            <BulkUploadQueue />
            <ContractorNotesPanel />
            <TeamComingSoonCard />
        </div>
    );
}

// ============ Calibrator badge + opt-in ============
function CalibratorBadgeCard() {
    const { user, refresh } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetchCalibrationShareStats();
                if (!cancelled) setStats(r);
            } catch {
                if (!cancelled) setStats({ locked: true });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const toggle = async (next) => {
        setSaving(true);
        try {
            await updateNotificationPrefs({ calibration_share_opt_in: next });
            const r = await fetchCalibrationShareStats();
            setStats(r);
            await refresh?.();
            toast.success(
                next
                    ? "Thanks — your anonymized actuals will help everyone's estimates."
                    : "Calibration share-back paused.",
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not update.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;
    const optedIn = !!stats?.opt_in;
    const isCalibrator = !!stats?.is_calibrator;

    return (
        <div
            className="bl-card p-5 md:p-6 bg-bl-teal-tint/20 relative overflow-hidden"
            data-testid="calibrator-card"
        >
            <div className="flex items-start gap-4 flex-wrap">
                <div className="w-10 h-10 rounded-lg bg-bl-surface flex items-center justify-center shrink-0">
                    <Award className="w-5 h-5 text-bl-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-semibold text-bl text-lg leading-tight">
                            Calibration share-back
                        </h3>
                        {isCalibrator && (
                            <span
                                className="bl-badge bl-badge-medium inline-flex items-center gap-1"
                                data-testid="calibrator-badge"
                            >
                                <Award className="w-3 h-3" /> Calibrator
                            </span>
                        )}
                    </div>
                    <p className="mt-1.5 text-sm text-bl-muted leading-relaxed">
                        When you log an actual contractor quote, share an
                        anonymized copy back to the public estimator. Only ZIP,
                        category, estimated range, actual amount, and date are
                        shared — never your account, the contractor name, or
                        notes.
                    </p>
                    {stats?.my_contribution_count > 0 && (
                        <p className="mt-2 text-xs text-bl">
                            You've contributed{" "}
                            <span className="font-mono-bl">
                                {stats.my_contribution_count}
                            </span>{" "}
                            data point{stats.my_contribution_count === 1 ? "" : "s"}{" "}
                            so far.{" "}
                            <span className="text-bl-muted">
                                Community total: {stats.global_total}
                            </span>
                        </p>
                    )}
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={optedIn}
                        disabled={saving}
                        onChange={(e) => toggle(e.target.checked)}
                        data-testid="calibration-share-toggle"
                    />
                    <div className="w-11 h-6 bg-bl-bg border border-bl rounded-full peer peer-checked:bg-bl-primary peer-checked:border-bl-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-bl-surface after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
            </div>
            {stats?.top_categories && Object.keys(stats.top_categories).length > 0 && (
                <div className="mt-4 pt-4 border-t border-bl">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted mb-2">
                        Top contributed categories
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {Object.entries(stats.top_categories).map(([cat, n]) => (
                            <span
                                key={cat}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bl-surface text-xs text-bl"
                            >
                                {cat}{" "}
                                <span className="text-bl-muted font-mono-bl">
                                    {n}
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ============ Portfolio export ============
function PortfolioExportCard() {
    return (
        <div className="bl-card p-5 md:p-6 flex items-center gap-4 flex-wrap" data-testid="portfolio-export-card">
            <div className="w-10 h-10 rounded-lg bg-bl-teal-tint flex items-center justify-center shrink-0">
                <FileStack className="w-5 h-5 text-bl-primary" />
            </div>
            <div className="flex-1 min-w-0">
                <h3 className="font-display font-semibold text-bl text-lg">
                    Portfolio export bundle
                </h3>
                <p className="mt-1 text-sm text-bl-muted leading-relaxed">
                    Download a single ZIP containing your pipeline, quote
                    tracking, and calibration ledger as separate CSVs.
                </p>
            </div>
            <a
                href={portfolioExportZipUrl()}
                className="bl-btn-primary text-sm shrink-0"
                data-testid="portfolio-export-download-btn"
            >
                <Download className="w-4 h-4" /> Download .zip
            </a>
        </div>
    );
}

// ============ Bulk upload queue ============
function BulkUploadQueue() {
    const [data, setData] = useState({ jobs: [], counts: {}, statuses: [] });
    const [loading, setLoading] = useState(true);
    const [dragging, setDragging] = useState(false);

    const refresh = async () => {
        try {
            const r = await listBulkUploads();
            setData(r);
        } catch {
            setData({ jobs: [], counts: {}, statuses: [] });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const onDrop = async (e) => {
        e.preventDefault();
        setDragging(false);
        const files = [...(e.dataTransfer?.files || [])].filter(
            (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
        );
        if (!files.length) {
            toast.error("Drop PDF files only.");
            return;
        }
        if (files.length > 25) {
            toast.error("Up to 25 files per batch.");
            return;
        }
        try {
            const created = await enqueueBulkUploads(
                files.map((f) => ({ file_name: f.name, file_size: f.size })),
            );
            toast.success(`Queued ${created.length} file${created.length === 1 ? "" : "s"}.`);
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not enqueue files.");
        }
    };

    const onPickFiles = async (e) => {
        const files = [...(e.target.files || [])];
        if (!files.length) return;
        try {
            const created = await enqueueBulkUploads(
                files.map((f) => ({ file_name: f.name, file_size: f.size })),
            );
            toast.success(`Queued ${created.length} file${created.length === 1 ? "" : "s"}.`);
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not enqueue files.");
        } finally {
            e.target.value = "";
        }
    };

    const onDelete = async (bjid) => {
        try {
            await deleteBulkUploadJob(bjid);
            setData((d) => ({
                ...d,
                jobs: d.jobs.filter((j) => j.bulk_job_id !== bjid),
            }));
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not remove.");
        }
    };

    return (
        <div data-testid="bulk-upload-queue">
            <h3 className="font-display font-semibold text-bl text-lg flex items-center gap-2 mb-3">
                <Upload className="w-5 h-5 text-bl-primary" />
                Bulk upload queue
            </h3>
            <label
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={
                    "bl-card p-6 md:p-8 text-center cursor-pointer transition-colors block " +
                    (dragging ? "border-bl-primary bg-bl-teal-tint/30" : "")
                }
                data-testid="bulk-upload-dropzone"
            >
                <input
                    type="file"
                    multiple
                    accept="application/pdf,.pdf"
                    onChange={onPickFiles}
                    className="hidden"
                    data-testid="bulk-upload-file-input"
                />
                <Upload className="w-6 h-6 text-bl-primary mx-auto mb-2" />
                <p className="text-sm text-bl">
                    Drop up to 25 PDFs to queue them, or click to browse.
                </p>
                <p className="mt-1 text-xs text-bl-muted">
                    Files are processed one at a time. You'll be notified as
                    each report becomes ready.
                </p>
            </label>
            {loading ? (
                <p className="mt-3 text-sm text-bl-muted">Loading queue…</p>
            ) : data.jobs.length === 0 ? (
                <p className="mt-3 text-xs text-bl-muted">
                    No batches queued yet.
                </p>
            ) : (
                <div
                    className="mt-3 bl-card overflow-x-auto"
                    data-testid="bulk-upload-list"
                >
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-bl text-left">
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">File</th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">Status</th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">Report</th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">Queued</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.jobs.map((j) => (
                                <tr
                                    key={j.bulk_job_id}
                                    className="border-b border-bl last:border-0"
                                    data-testid={`bulk-row-${j.bulk_job_id}`}
                                >
                                    <td className="px-4 py-3 truncate max-w-[14rem]">
                                        {j.file_name}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="px-2 py-0.5 rounded-full bg-bl-teal-tint/50 text-bl-primary text-xs">
                                            {j.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {j.report_id ? (
                                            <Link
                                                to={`/report/${j.report_id}`}
                                                className="text-bl-primary hover:underline"
                                            >
                                                Open
                                            </Link>
                                        ) : (
                                            <span className="text-bl-muted">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-bl-muted font-mono-bl">
                                        {new Date(j.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => onDelete(j.bulk_job_id)}
                                            className="text-bl-muted p-1.5 rounded hover:bg-[#C85B3A]/10 hover:text-[#C85B3A]"
                                            aria-label="Remove from queue"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ============ Saved contractor notes ============
const EMPTY_CN_DRAFT = {
    contractor_name: "",
    category: "",
    rating: 4,
    notes: "",
};

function ContractorNotesPanel() {
    const [data, setData] = useState({ notes: [] });
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(EMPTY_CN_DRAFT);
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await listContractorNotes();
                setData(r);
            } catch {
                /* empty */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const onCreate = async (e) => {
        e.preventDefault();
        if (!draft.contractor_name.trim() || !draft.notes.trim()) {
            toast.error("Contractor name and notes are required.");
            return;
        }
        setSubmitting(true);
        try {
            const created = await createContractorNote({
                contractor_name: draft.contractor_name.trim(),
                category: draft.category.trim() || null,
                rating: Number(draft.rating) || null,
                notes: draft.notes.trim(),
            });
            setData((d) => ({ ...d, notes: [created, ...d.notes] }));
            setDraft(EMPTY_CN_DRAFT);
            toast.success("Contractor saved.");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not save.");
        } finally {
            setSubmitting(false);
        }
    };

    const onSaveEdit = async (cnid, patch) => {
        try {
            const updated = await updateContractorNote(cnid, patch);
            setData((d) => ({
                ...d,
                notes: d.notes.map((n) => (n.contractor_note_id === cnid ? updated : n)),
            }));
            setEditingId(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not update.");
        }
    };

    const onDelete = async (cnid) => {
        if (!confirm("Delete this contractor note?")) return;
        try {
            await deleteContractorNote(cnid);
            setData((d) => ({
                ...d,
                notes: d.notes.filter((n) => n.contractor_note_id !== cnid),
            }));
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not delete.");
        }
    };

    return (
        <div data-testid="contractor-notes-panel">
            <h3 className="font-display font-semibold text-bl text-lg flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-bl-primary" />
                Saved contractor notes
            </h3>
            <form
                onSubmit={onCreate}
                className="bl-card p-4 md:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
                data-testid="contractor-notes-form"
            >
                <input
                    type="text"
                    placeholder="Contractor name *"
                    required
                    value={draft.contractor_name}
                    onChange={(e) => setDraft({ ...draft, contractor_name: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="cn-input-name"
                />
                <input
                    type="text"
                    placeholder="Category"
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="cn-input-category"
                />
                <select
                    value={draft.rating}
                    onChange={(e) => setDraft({ ...draft, rating: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="cn-input-rating"
                >
                    {[1, 2, 3, 4, 5].map((r) => (
                        <option key={r} value={r}>
                            {r} star{r === 1 ? "" : "s"}
                        </option>
                    ))}
                </select>
                <button
                    type="submit"
                    disabled={submitting}
                    className="bl-btn-primary text-sm"
                    data-testid="cn-add-btn"
                >
                    <Plus className="w-4 h-4" /> Add
                </button>
                <textarea
                    placeholder="Notes (strengths, response time, gotchas…)"
                    value={draft.notes}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    rows={2}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm sm:col-span-2 lg:col-span-4 resize-y"
                    maxLength={4000}
                    data-testid="cn-input-notes"
                />
            </form>
            {loading ? null : data.notes.length === 0 ? (
                <p className="mt-3 text-xs text-bl-muted">
                    No contractor notes yet. Save trusted contacts here to
                    reuse across properties.
                </p>
            ) : (
                <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="contractor-notes-list">
                    {data.notes.map((n) => {
                        const editing = editingId === n.contractor_note_id;
                        return (
                            <ContractorNoteRow
                                key={n.contractor_note_id}
                                note={n}
                                editing={editing}
                                onEdit={() => setEditingId(n.contractor_note_id)}
                                onCancel={() => setEditingId(null)}
                                onSave={(patch) => onSaveEdit(n.contractor_note_id, patch)}
                                onDelete={() => onDelete(n.contractor_note_id)}
                            />
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function ContractorNoteRow({ note, editing, onEdit, onCancel, onSave, onDelete }) {
    const [draft, setDraft] = useState({
        contractor_name: note.contractor_name,
        category: note.category || "",
        rating: note.rating || 4,
        notes: note.notes,
    });
    return (
        <li
            className="bl-card p-4 bg-bl-bg/40"
            data-testid={`contractor-note-${note.contractor_note_id}`}
        >
            {editing ? (
                <div className="space-y-2">
                    <input
                        type="text"
                        value={draft.contractor_name}
                        onChange={(e) =>
                            setDraft({ ...draft, contractor_name: e.target.value })
                        }
                        className="w-full px-2 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    />
                    <div className="flex gap-2">
                        <input
                            type="text"
                            placeholder="Category"
                            value={draft.category}
                            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                            className="flex-1 px-2 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                        />
                        <select
                            value={draft.rating}
                            onChange={(e) => setDraft({ ...draft, rating: Number(e.target.value) })}
                            className="px-2 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                        >
                            {[1, 2, 3, 4, 5].map((r) => (
                                <option key={r} value={r}>
                                    {r}★
                                </option>
                            ))}
                        </select>
                    </div>
                    <textarea
                        value={draft.notes}
                        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        rows={3}
                        className="w-full px-2 py-1.5 rounded-md border border-bl bg-bl-surface text-bl text-sm resize-y"
                    />
                    <div className="flex gap-2 justify-end">
                        <button
                            onClick={onCancel}
                            className="text-bl-muted p-1.5 rounded hover:bg-bl-bg"
                            aria-label="Cancel"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => onSave(draft)}
                            className="text-bl-primary p-1.5 rounded hover:bg-bl-teal-tint"
                            aria-label="Save changes"
                        >
                            <Check className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-display font-medium text-bl">
                                    {note.contractor_name}
                                </span>
                                {note.category && (
                                    <span className="text-xs text-bl-muted">
                                        · {note.category}
                                    </span>
                                )}
                            </div>
                            {note.rating != null && (
                                <div className="mt-0.5 text-xs text-bl-primary">
                                    {"★".repeat(note.rating)}
                                    <span className="text-bl-muted">
                                        {"☆".repeat(5 - note.rating)}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            <button
                                onClick={onEdit}
                                className="text-bl-muted p-1.5 rounded hover:bg-bl-teal-tint hover:text-bl-primary"
                                aria-label="Edit"
                            >
                                <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                                onClick={onDelete}
                                className="text-bl-muted p-1.5 rounded hover:bg-[#C85B3A]/10 hover:text-[#C85B3A]"
                                aria-label="Delete"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <p className="mt-2 text-sm text-bl whitespace-pre-wrap leading-relaxed">
                        {note.notes}
                    </p>
                </>
            )}
        </li>
    );
}

// ============ Team coming soon ============
function TeamComingSoonCard() {
    const [team, setTeam] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const r = await fetchInvestorTeamInfo();
                setTeam(r);
            } catch {
                setTeam(null);
            }
        })();
    }, []);
    if (!team || team.locked) return null;
    return (
        <div className="bl-card p-5 md:p-6 bg-bl-teal-tint/15" data-testid="team-coming-soon-card">
            <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-bl-surface flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5 text-bl-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-semibold text-bl text-lg leading-tight">
                            {team.headline}
                        </h3>
                        <span className="bl-badge bl-badge-neutral inline-flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Coming soon
                        </span>
                    </div>
                    <p className="mt-1.5 text-sm text-bl-muted leading-relaxed">
                        {team.description}
                    </p>
                </div>
            </div>
        </div>
    );
}
